import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  Select,
  MenuItem,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import { CloseOutlined } from "@ant-design/icons";
import "../css/AddNewFBODialog.css";
import useFBOApi from "../../services/api/useFBOApi";
import { useMutation } from "@tanstack/react-query";
import { DATE_FORMAT, TIME_FORMAT, API_DATE_FORMAT, API_TIME_FORMAT, API_DATETIME_FORMAT } from "../../utils/constants";
import { MobileDatePicker, MobileTimePicker } from "@mui/x-date-pickers";
import DateLocalizationProvider from "./DateLocalizationProvider";
import SnackbarAlert from "./SnackbarAlert";
import SearchAirportDialog from "./SearchAirportDialog";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const AddNewLandingFeeDialog = ({
  open,
  onClickSave,
  onClose,
  isLoading,
  fboUserData,
  aircraftData,
  originAirportList,
  destinationAirportList,
}) => {

  const [registrationNumber, setRegistrationNumber] = useState("");
  const [selectedAircraft, setSelectedAircraft] = useState(null);
  const [aircraftOwnerName, setAircraftOwnerName] = useState("");
  const [originAirport, setOriginAirport] = useState(null);
  const [destinationAirport, setDestinationAirport] = useState(null);
  const [destinationTerminal, setDestinationTerminal] = useState(null);
  const [associatedTerminals, setAssociatedTerminals] = useState([]);
  const [landingDateTime, setLandingDateTime] = useState({});
  const [showAirportError, setShowAirportError] = useState(false);
  const [showSearchAirportDialog, setShowSearchAirportDialog] = useState(false);

  useEffect(() => {
    setDefaultValue();
  }, [open]);

  // Auto-populate destination airport if only one option is available
  useEffect(() => {
    if (destinationAirportList?.length === 1 && !destinationAirport) {
      const singleAirport = destinationAirportList[0];
      const filteredTerminals = singleAirport?.terminals?.filter(
        (terminal) => terminal?.fboId === fboUserData?.associations[0]?.fboId
      );
      setDestinationAirport(singleAirport);
      setAssociatedTerminals(filteredTerminals);

      // Auto-select terminal if only one is available
      if (filteredTerminals?.length === 1) {
        setDestinationTerminal(filteredTerminals[0]);
      }
    }
  }, [destinationAirportList, destinationAirport, fboUserData]);

  const setDefaultValue = () => {
    setRegistrationNumber("");
    setSelectedAircraft(null);
    setAircraftOwnerName("");
    setOriginAirport(null);
    setDestinationAirport(null);
    setDestinationTerminal(null);
    setLandingDateTime({});
    setShowAirportError(false);
    setShowSearchAirportDialog(false);
  };



  const handleAircraftChange = (event) => {
    const selectedId = event.target.value;
    const selectedAircraft = aircraftData?.find(
      (aircraft) => aircraft?.id === selectedId
    );
    setSelectedAircraft(selectedAircraft);
    setAircraftOwnerName("");
  };

  const handleSelectAirportClick = () => {
    setShowSearchAirportDialog(true);
  };

  const handleAirportOnSelect = (airport) => {
    setOriginAirport(airport);
    setShowAirportError(false);
  };

  const handleDestinationAirportChange = (event) => {
    const selectedId = event.target.value;
    const selectedAirport = destinationAirportList?.find(
      (airport) => airport?.id === selectedId
    );
    const filteredTerminals = selectedAirport?.terminals?.filter(
      (terminal) => terminal?.fboId === fboUserData?.associations[0]?.fboId
    );
    setDestinationAirport(selectedAirport);
    setAssociatedTerminals(filteredTerminals);
    setDestinationTerminal(null);
    setShowAirportError(false);
  };

  const handleDestinationTerminalChange = (event) => {
    const selectedId = event.target.value;
    const selectedTerminal = associatedTerminals.find(
      (terminal) => terminal?.id === selectedId
    );
    setDestinationTerminal(selectedTerminal);
  };

  const handleAircraftSearchChange = (event) => {
    setRegistrationNumber(event.target.value);
  };

  const handleAircraftOwnerNameChange = (event) => {
    setAircraftOwnerName(event.target.value);
  };



  const handleLandingDateChange = (newValue) => {
    setLandingDateTime((prev) => ({
      ...prev,
      date: newValue,
    }));
  };

  const handleLandingTimeChange = (newValue) => {
    setLandingDateTime((prev) => ({
      ...prev,
      time: newValue,
    }));
  };

  const getDateAndTime = (date, time) => {
    if (date && time) {
      // Create a local datetime by combining date and time
      const dateOnly = dayjs(date).format(API_DATE_FORMAT);
      const timeOnly = dayjs(time).format(API_TIME_FORMAT);
      const localDateTime = dayjs(`${dateOnly}T${timeOnly}`);

      // Convert to UTC and format as ISO string
      return localDateTime.utc().format(API_DATETIME_FORMAT);
    } else {
      return null;
    }
  };

  const handleSaveClick = () => {
    // Check if origin and destination airports are the same
    if (originAirport?.id === destinationAirport?.id) {
      setShowAirportError(true);
      return;
    }

    const newLandingFeeData = {
      aircraftRegistrationNumber: registrationNumber.toUpperCase(),
      aircraftTypeId: selectedAircraft?.id,
      aircraftOwnerName: aircraftOwnerName,
      departureAirportId: originAirport?.id,
      arrivalAirportId: destinationAirport?.id,
      arrivalAirportTerminalId: destinationTerminal?.id,
      arrivalTime: getDateAndTime(landingDateTime?.date, landingDateTime?.time),
    };
    onClickSave(newLandingFeeData);
  };

  const handleCloseError = () => {
    setShowAirportError(false);
  };

  const isValidToAdd = () => {
    return (
      !registrationNumber.trim() ||
      !selectedAircraft ||
      !aircraftOwnerName.trim() ||
      !originAirport ||
      !destinationAirport ||
      !destinationTerminal ||
      !landingDateTime.date ||
      !landingDateTime.time
    );
  };

  return (
    <>
      {showAirportError && (
        <SnackbarAlert type={"error"} onClose={handleCloseError}>
          Origin airport and destination airport cannot be the same
        </SnackbarAlert>
      )}
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          className: "add-new-fbo-dialog-container",
        }}
      >
        <DialogContent>
          <CloseOutlined className="add-new-fbo-dialog-close" onClick={onClose} />
          <div className="add-new-fbo-dialog-content">
            <div className="add-new-fbo-dialog-title">
              Add Trip in Landing Fee
            </div>
            <div className="add-new-fbo-registration-container">
              <label>Aircraft Registration Number</label>
              <input
                name="search-aircraft-by-registration-number"
                placeholder="Enter here"
                value={registrationNumber}
                onChange={handleAircraftSearchChange}
                className="add-new-fbo-registration-input"
              ></input>
            </div>
            <div className="add-new-fbo-input-container">
              <label>Aircraft Type</label>
              <Select
                id="add-new-landing-fee-aircraft-select"
                className="add-new-fbo-select-container"
                value={selectedAircraft?.id ?? ""}
                onChange={handleAircraftChange}
                renderValue={(selected) =>
                  aircraftData?.find((aircraft) => aircraft?.id === selected)
                    ?.icaoCode
                }
              >
                {aircraftData?.map((aircraft) => (
                  <MenuItem key={aircraft?.id} value={aircraft?.id}>
                    <ListItemText
                      primary={`${aircraft?.icaoCode} (${aircraft?.engineType})`}
                    />
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="add-new-fbo-input-container">
              <label>Aircraft MTOW</label>
              <div className="add-new-fbo-non-editable-field">
                {selectedAircraft?.maxTakeOffWeight}
              </div>
            </div>
            <div className="add-new-fbo-input-container">
              <label>Aircraft Owner Name</label>
              <input
                name="aircraft-owner-name-input"
                placeholder="Enter here"
                value={aircraftOwnerName}
                onChange={handleAircraftOwnerNameChange}
              ></input>
            </div>
            <div className="add-new-fbo-input-container">
              <label>Origin Airport</label>
              <div
                className="add-new-fbo-airport-search-container"
                onClick={handleSelectAirportClick}
              >
                {originAirport?.name ? (
                  <span className="add-new-fbo-airport-search-text">{originAirport?.name}</span>
                ) : (
                  <span className="add-new-fbo-airport-search-placeholder">Search</span>
                )}
              </div>
            </div>
            <div className="add-new-fbo-input-container">
              <label>Destination Airport</label>
              <Select
                id="add-new-landing-fee-origin-airport"
                className="add-new-fbo-select-container"
                value={destinationAirport?.id ?? ""}
                onChange={handleDestinationAirportChange}
                renderValue={(selected) =>
                  destinationAirportList?.find(
                    (airport) => airport?.id === selected
                  )?.name
                }
              >
                {destinationAirportList?.map((airport) => (
                  <MenuItem key={airport?.id} value={airport?.id}>
                    <ListItemText primary={airport?.name} />
                  </MenuItem>
                ))}
              </Select>
            </div>
            <div className="add-new-fbo-input-container">
              <label>Destination Terminal</label>
              <Select
                id="add-new-landing-fee-origin-airport"
                className="add-new-fbo-select-container"
                value={destinationTerminal?.id ?? ""}
                disabled={!destinationAirport}
                onChange={handleDestinationTerminalChange}
                renderValue={(selected) =>
                  associatedTerminals.find(
                    (terminal) => terminal?.id === selected
                  )?.name
                }
              >
                {associatedTerminals.map((terminal) => (
                  <MenuItem key={terminal?.id} value={terminal?.id}>
                    <ListItemText primary={terminal?.name} />
                  </MenuItem>
                ))}
              </Select>
            </div>
            <DateLocalizationProvider>
              <div className="add-new-fbo-date-time-container">
                <label>Date of Landing</label>
                <MobileDatePicker
                  id="fbo-landing-fee-date"
                  className="add-new-fbo-date-time-picker"
                  format={DATE_FORMAT}
                  maxDate={dayjs()}
                  value={landingDateTime?.date}
                  onChange={handleLandingDateChange}
                />
              </div>
              <div className="add-new-fbo-date-time-container">
                <label>Time of Landing</label>
                <MobileTimePicker
                  id="fbo-landing-fee-time"
                  className="add-new-fbo-date-time-picker"
                  format={TIME_FORMAT}
                  value={landingDateTime?.time}
                  onChange={handleLandingTimeChange}
                />
              </div>
            </DateLocalizationProvider>
            <Button
              variant="contained"
              onClick={!isLoading ? handleSaveClick : null}
              disabled={isValidToAdd()}
              endIcon={
                isLoading ? <CircularProgress size={30} color="inherit" /> : null
              }
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <SearchAirportDialog
        open={showSearchAirportDialog}
        onClose={() => setShowSearchAirportDialog(false)}
        airportData={originAirportList}
        onSelectAirport={handleAirportOnSelect}
      />
    </>
  );
};

export default AddNewLandingFeeDialog;
